import React, { useMemo, useState } from "react";
import { Add, Check, Close, Create } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  styled,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import {
  addElement,
  updateElement,
} from "../../../reducers/configurator/configuratorSlice";
import { myExpariencesService } from "../../../services";
import ResponsiveDialogManager from "../../dialog/ResponsiveDialogManager";
import ImageCropper from "../../imageCropper/ImageCropper";
import { useSnackbar } from "notistack";

const PREFIX = "gameLogosForm";

const classes = {
  root: `${PREFIX}-root`,
  loader: `${PREFIX}-loader`,
  logo: `${PREFIX}-logo`,
  imageContainer: `${PREFIX}-imageContainer`,
  iconsContainer: `${PREFIX}-iconsContainer`,
  buttonSpaces: `${PREFIX}-buttonSpaces`,
};

const GameLogosFormContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      width: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    [`& .${classes.loader}`]: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    [`& .${classes.logo}`]: {
      height: "80px",
      maxWidth: "200px",
      objectFit: "contain",
    },
    [`& .${classes.imageContainer}`]: {
      width: "100%",
      position: "relative",
      minWidth: "100px",
      display: "flex",
      justifyContent: "center",
      marginBottom: "1rem",
    },
    [`& .${classes.iconsContainer}`]: {
      display: "flex",
      flexDirection: "column-reverse",
      alignItems: "center",
    },
    [`& .${classes.buttonSpaces}`]: {
      backgroundColor: "#424242",
      color: theme.palette.primary.main,
      marginRight: "15px",
      "&:hover": {
        backgroundColor: "rgba(66,66,66,0.7)",
        color: theme.palette.primary.main,
      },
    },
  };
});

const GameLogosForm = ({ logoKey, isPickleball = false }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [urlToCrop, setUrlToCrop] = useState({
    url: null,
    key: null,
  });
  const isAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.role === "ADMIN"
  );
  const currentTile = useSelector((state) => state.configurator.selectedTile);
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const myTile = useSelector((state) =>
    currentTile
      ? state.configurator.grid[parentLayout][currentTile.row][
          currentTile.column
        ]
      : null
  );

  const [uploading, setUploading] = useState({
    groundLogo: false,
    basketLogo: false,
    ballLogo: false,
    leaderboardLogo: false,
    socleLogo: false,
  });

  const handleAddFile = (url, key) => {
    dispatch(
      addElement({
        layer: "middle",
        data: { ...myTile.middle, [key]: url },
        tile: { row: myTile.row, column: myTile.column },
      })
    );
  };

  const HandleRemoveFile = (key) => {
    dispatch(
      updateElement({
        layer: "middle",
        data: { ...myTile.middle, [key]: null },
        tile: currentTile,
      })
    );
  };

  const getImageFromS3 = (key, file, blobName) => {
    setUploading({ ...uploading, [key]: true });
    myExpariencesService
      .getImageUrlFromS3(isAdmin, id, file, true, blobName)
      .then((res) => {
        handleAddFile(res.url, key);
        setUploading({ ...uploading, [key]: false });
        setUrlToCrop({ url: null, key: null });
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), { variant: "error" });
        setUploading({ ...uploading, [key]: false });
      });
  };

  const aspectRatios = useMemo(
    () => ({
      groundLogo: 1 / 1,
      basketLogo: 7 / 4,
      ballLogo: 15 / 8,
      leaderboardLogo: 1 / 1,
      socleLogo: 1 / 1,
      courtLogo: 16 / 10,
      pickleballCourt: 2.16,
      netLogo: 1 / 1,
      netBandLogo: 1 / 0.16,
      netPoleLogo: 1 / 6.43,
      launcherLogo: 1 / 1,
    }),
    []
  );

  return (
    <GameLogosFormContainer>
      <ResponsiveDialogManager
        title={t("myExpariences.form.cropImage")}
        fullScreen
        open={urlToCrop.url ? true : false}
        onClose={() => {
          setUrlToCrop({ url: null, key: null });
        }}
        renderer={
          <ImageCropper
            imgUrl={urlToCrop.url}
            urlSetter={setUrlToCrop}
            urlToCrop={urlToCrop}
            aspect={
              aspectRatios[isPickleball ? "pickleballCourt" : urlToCrop.key]
            }
            isCourt={logoKey === "courtLogo"}
            isPickleball={isPickleball}
          />
        }
        toolbarActions={
          <GameLogosFormContainer>
            <IconButton
              className={classes.buttonSpaces}
              disabled={uploading[urlToCrop.key]}
              onClick={async () => {
                const myCroppedImage = document
                  .getElementById("cropping-canvas")
                  .toDataURL();
                const finalExtension = myCroppedImage.substring(
                  "data:image/".length,
                  myCroppedImage.indexOf(";base64")
                );
                const blobName = `${
                  urlToCrop.key
                }-${Date.now()}.${finalExtension}`;
                getImageFromS3(urlToCrop.key, myCroppedImage, blobName);
              }}
            >
              {uploading[urlToCrop.key] ? (
                <CircularProgress size={28} sx={{ color: "black" }} />
              ) : (
                <Check />
              )}
            </IconButton>
          </GameLogosFormContainer>
        }
      />
      <div className={classes.root}>
        {uploading[logoKey] ? (
          <div className={classes.loader}>
            <CircularProgress sx={{ margin: "0 0 1rem 1rem" }} />
            <Typography variant="body2" sx={{ marginLeft: "0.5rem" }}>
              {t("myExpariences.form.basketUploading")}
            </Typography>
          </div>
        ) : !myTile.middle[logoKey] ? (
          <Box>
            <Button
              variant="outlined"
              component="label"
              id={`logo-input-${logoKey}`}
              startIcon={<Add />}
              name={`select-${logoKey}-logo`}
              onClick={(e) => (e.target.value = null)}
              onChange={(e) =>
                setUrlToCrop({
                  url: URL.createObjectURL(e.target.files[0]),
                  key: logoKey,
                })
              }
            >
              {t("myExpariences.form.basketAddFile")}
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.psd,.tiff,.tga,.bmp,.iff,.pict"
                hidden
              />
            </Button>
          </Box>
        ) : (
          <div className={classes.imageContainer}>
            <img
              src={myTile.middle[logoKey]}
              alt={`img-${logoKey}`}
              className={classes.logo}
            />
            <div className={classes.iconsContainer}>
              <IconButton
                color="primary"
                aria-label="add ground logo"
                component="span"
                variant="contained"
                onClick={() => HandleRemoveFile(logoKey)}
              >
                <Close />
              </IconButton>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.psd,.tiff,.tga,.bmp,.iff,.pict"
                name={`logo-input-modify-${logoKey}`}
                id={`logo-input-modify-${logoKey}`}
                style={{ display: "none" }}
                onClick={(e) => (e.target.value = null)}
                onChange={(e) =>
                  setUrlToCrop({
                    url: URL.createObjectURL(e.target.files[0]),
                    key: logoKey,
                  })
                }
              />
              <label htmlFor={`logo-input-modify-${logoKey}`}>
                <IconButton
                  color="primary"
                  aria-label="add ground logo"
                  component="span"
                  variant="contained"
                  className={classes.iconButtonColor}
                >
                  <Create className={classes.iconButtonInsideColor} />
                </IconButton>
              </label>
            </div>
          </div>
        )}
      </div>
    </GameLogosFormContainer>
  );
};

export default GameLogosForm;
